/**
 * Flagging rule save, activate, inactivate, and test workflow.
 */

import {
  BLOCK_OPTION_OPTIONS,
  DUPLICATE_CHECK_OPTIONS,
  FLAG_DETAIL_REQUIREMENT_OPTIONS,
  FLAG_TYPE_OPTIONS,
  FLAGGING_AVAILABILITY_OPTIONS,
  FLAGGING_SUBMITTED_BY_OPTIONS,
  SUBMISSION_RESULT_OPTIONS,
  findConflictingFlaggingRule,
  findDuplicateFlaggingRule,
  getAvailabilityOptionsForFlagType,
  getBlockOfferingReasonOptions,
  getDetailRequiredReasonOptions,
  getFlagReasonSetLabel,
  getFlagReasonSetOptions,
  getFlagTypeLabel,
  getReasonItemsForSet,
  labelFromOptions,
  labelsFromValues,
  needsBlockOfferingReasons,
  needsDetailRequiredReasons,
  normalizeFlaggingForm,
  normalizeFlaggingRuleStatus,
  normalizeFlagType,
  supportsBlockOption,
} from './flaggingConfig';
import { emitConfigurationHistoryBatch } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

const TRACKED_FIELDS = [
  'ruleName',
  'flagType',
  'submittedBy',
  'flaggingAvailability',
  'flagReasonSet',
  'flagDetailRequirement',
  'detailRequiredReasons',
  'duplicateCheck',
  'blockOption',
  'blockOfferingReasons',
  'submissionResult',
];

export function flaggingRuleActor() {
  return { ...ACTOR };
}

export function ruleSnapshotFromForm(formData) {
  return normalizeFlaggingForm(formData);
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeFlaggingForm(published);
}

export function getEditRuleSource(rule, { preferDraft = false } = {}) {
  if (!rule) return null;
  if (preferDraft && rule.pendingDraft) {
    return {
      ...rule,
      ...rule.pendingDraft,
      id: rule.id,
      version: rule.version,
      status: 'draft',
      pendingDraft: rule.pendingDraft,
      configurationHistory: rule.configurationHistory,
      testHistory: rule.testHistory,
      createdOn: rule.createdOn,
      createdBy: rule.createdBy,
      _editingActiveRevision: true,
    };
  }
  return { ...normalizeFlaggingForm(rule), _editingActiveRevision: normalizeFlaggingRuleStatus(rule.status) === 'active' };
}

function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    flagType: 'Flag Type',
    submittedBy: 'Flag Submitted By',
    flaggingAvailability: 'Flagging Availability',
    flagReasonSet: 'Flag Reason Set',
    flagDetailRequirement: 'Flag Detail Requirement',
    detailRequiredReasons: 'Detail-Required Reasons',
    duplicateCheck: 'Duplicate Check',
    blockOption: 'Block Option',
    blockOfferingReasons: 'Block-Offering Reasons',
    submissionResult: 'Submission Result',
    status: 'Status',
  };
  return labels[key] || key;
}

function displayFieldValue(key, value, context = {}) {
  if (value == null || value === '') return '—';
  if (key === 'flagType') return getFlagTypeLabel(value);
  if (key === 'submittedBy') return labelsFromValues(FLAGGING_SUBMITTED_BY_OPTIONS, value).join(', ') || '—';
  if (key === 'flaggingAvailability') return labelFromOptions(FLAGGING_AVAILABILITY_OPTIONS, value);
  if (key === 'flagReasonSet') return getFlagReasonSetLabel(value);
  if (key === 'flagDetailRequirement') return labelFromOptions(FLAG_DETAIL_REQUIREMENT_OPTIONS, value);
  if (key === 'detailRequiredReasons') {
    return labelsFromValues(getDetailRequiredReasonOptions(context.flagReasonSet), value).join(', ') || '—';
  }
  if (key === 'duplicateCheck') return labelFromOptions(DUPLICATE_CHECK_OPTIONS, value);
  if (key === 'blockOption') return labelFromOptions(BLOCK_OPTION_OPTIONS, value);
  if (key === 'blockOfferingReasons') {
    return labelsFromValues(getBlockOfferingReasonOptions(context.flagReasonSet), value).join(', ') || '—';
  }
  if (key === 'submissionResult') return labelFromOptions(SUBMISSION_RESULT_OPTIONS, value);
  return String(value);
}

export function diffRuleSnapshots(prev, next) {
  const changes = [];
  TRACKED_FIELDS.forEach((key) => {
    const oldDisplay = displayFieldValue(key, prev?.[key], prev || {});
    const newDisplay = displayFieldValue(key, next?.[key], next || {});
    if (oldDisplay !== newDisplay) {
      changes.push({ field: fieldLabel(key), previousValue: oldDisplay, newValue: newDisplay });
    }
  });
  return changes;
}

export function appendConfigurationHistory(rule, action, changes, status, version) {
  const ts = new Date().toISOString();
  const actor = flaggingRuleActor();
  const base = Array.isArray(rule.configurationHistory) ? rule.configurationHistory : [];
  const changeRows = changes.length ? changes : [{ field: '—', previousValue: '—', newValue: '—' }];
  const entries = changeRows.map((c) => ({
    ruleId: rule.id,
    version,
    action,
    field: c.field,
    previousValue: c.previousValue,
    newValue: c.newValue,
    status,
    actor: actor.name,
    role: actor.role,
    accountId: actor.accountId,
    timestamp: ts,
  }));
  try {
    emitConfigurationHistoryBatch({
      rows: entries,
      module: 'Configuration — Flagging',
      sourceModuleRef: 'configuration_flagging',
      changedItem: rule.ruleName || rule.id,
      itemId: rule.id,
      actor: { name: actor.name, role: actor.role },
      stamp: ts,
      changeType: action === 'Add' || action === 'Created' ? 'Add' : 'Edit',
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  return [...base, ...entries];
}

export function appendTestHistory(rule, summary, passed) {
  const actor = flaggingRuleActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function collectActivationErrors(formData, allRules, excludeId) {
  const errors = [];
  const n = normalizeFlaggingForm(formData);
  const flagType = normalizeFlagType(n.flagType);

  if (!n.ruleName?.trim()) errors.push('Rule Name is required.');
  if (!flagType) errors.push('Flag Type is required.');
  if (!n.submittedBy?.length) errors.push('Flag Submitted By is required.');
  if (!n.flaggingAvailability) errors.push('Flagging Availability is required.');
  if (!n.flagReasonSet) errors.push('Flag Reason Set is required.');
  if (!n.flagDetailRequirement) errors.push('Flag Detail Requirement is required.');
  if (!n.duplicateCheck) errors.push('Duplicate Check is required.');
  if (!n.blockOption) errors.push('Block Option is required.');
  if (!n.submissionResult) errors.push('Submission Result is required.');

  const availOpts = getAvailabilityOptionsForFlagType(flagType);
  if (n.flaggingAvailability && !availOpts.some((o) => o.value === n.flaggingAvailability)) {
    errors.push('Flagging Availability does not match the Flag Type.');
  }

  const reasonSets = getFlagReasonSetOptions(flagType);
  if (n.flagReasonSet && !reasonSets.some((o) => o.value === n.flagReasonSet)) {
    errors.push('Flag Reason Set does not match the Flag Type.');
  }

  if (needsDetailRequiredReasons(n.flagDetailRequirement) && !n.detailRequiredReasons?.length) {
    errors.push('Detail-Required Reasons are required.');
  }

  if (needsDetailRequiredReasons(n.flagDetailRequirement)) {
    const allowed = new Set(getDetailRequiredReasonOptions(n.flagReasonSet).map((o) => o.value));
    if (n.detailRequiredReasons.some((r) => !allowed.has(r))) {
      errors.push('Selected detail-required reasons are not part of the reason set.');
    }
  }

  if (!supportsBlockOption(flagType) && n.blockOption === 'available_selected_reasons') {
    errors.push('Block Option is unsupported for the Flag Type.');
  }

  if (needsBlockOfferingReasons(n.blockOption) && !n.blockOfferingReasons?.length) {
    errors.push('Block-Offering Reasons are required.');
  }

  if (needsBlockOfferingReasons(n.blockOption)) {
    const allowed = new Set(getBlockOfferingReasonOptions(n.flagReasonSet).map((o) => o.value));
    if (n.blockOfferingReasons.some((r) => !allowed.has(r))) {
      errors.push('Selected Block-Offering Reasons are not part of the Message reason set.');
    }
  }

  if (flagType === 'general') {
    const specificOverlap = (allRules || []).find((r) => {
      if (excludeId && r.id === excludeId) return false;
      if (normalizeFlaggingRuleStatus(r.status) !== 'active') return false;
      const o = normalizeFlaggingForm(r);
      return ['post', 'review', 'message', 'quote'].includes(o.flagType);
    });
    if (specificOverlap) {
      errors.push('A General rule overlaps a supported specific Flag flow.');
    }
  }

  const dup = findDuplicateFlaggingRule(allRules, n, excludeId);
  if (dup) errors.push('An Active duplicate rule already exists for this configuration.');

  const conflict = findConflictingFlaggingRule(allRules, n, excludeId);
  if (conflict) errors.push('A conflicting Active rule exists with different eligibility or behaviour.');

  return errors;
}

export function runFlaggingRuleTest(formData, allRules) {
  const n = normalizeFlaggingForm(formData);
  const lines = [];
  const flagType = normalizeFlagType(n.flagType);

  lines.push(`Submitting account type: ${labelsFromValues(FLAGGING_SUBMITTED_BY_OPTIONS, n.submittedBy).join(', ') || '—'}`);
  lines.push(`Eligibility result: ${labelFromOptions(FLAGGING_AVAILABILITY_OPTIONS, n.flaggingAvailability) || '—'}`);
  lines.push(`Participant or interaction relationship: ${flagType === 'message' ? 'Own conversation participant check' : flagType === 'quote' ? 'Own quote thread participant check' : flagType === 'post' || flagType === 'review' ? 'Valid interaction check where required' : 'Signed-in account check'}`);
  lines.push(`Flag Reason Set: ${getFlagReasonSetLabel(n.flagReasonSet) || '—'}`);
  lines.push(`Detail requirement: ${labelFromOptions(FLAG_DETAIL_REQUIREMENT_OPTIONS, n.flagDetailRequirement) || '—'}`);
  if (needsDetailRequiredReasons(n.flagDetailRequirement)) {
    lines.push(`Detail-required reasons: ${labelsFromValues(getDetailRequiredReasonOptions(n.flagReasonSet), n.detailRequiredReasons).join(', ') || '—'}`);
  }
  lines.push(`Duplicate-check result: ${labelFromOptions(DUPLICATE_CHECK_OPTIONS, n.duplicateCheck) || '—'}`);
  lines.push(`Block Option result: ${labelFromOptions(BLOCK_OPTION_OPTIONS, n.blockOption) || '—'}`);
  if (needsBlockOfferingReasons(n.blockOption)) {
    lines.push(`Block-offering reasons: ${labelsFromValues(getBlockOfferingReasonOptions(n.flagReasonSet), n.blockOfferingReasons).join(', ') || '—'}`);
  }
  lines.push(`Submission Result: ${labelFromOptions(SUBMISSION_RESULT_OPTIONS, n.submissionResult) || '—'}`);

  const activationErrors = collectActivationErrors(n, allRules, n.id || undefined);

  if (activationErrors.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...activationErrors.map((c) => `• ${c}`)].join('\n'),
    };
  }

  return {
    passed: true,
    summary: 'Rule test passed. No Flag record, block, case, or assignment was created.',
    detail: [...lines, '', 'No duplicate or conflicting Active rules detected. Test history recorded.'].join('\n'),
  };
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  const isNew = !publishedBaseline?.id;
  const snapshot = ruleSnapshotFromForm(payload);
  const status = 'draft';

  if (isNew) {
    const record = {
      ...snapshot,
      id: payload.id,
      version: 1,
      status,
      pendingDraft: null,
      configurationHistory: appendConfigurationHistory(
        { id: payload.id, configurationHistory: [] },
        'Save Draft',
        diffRuleSnapshots({}, snapshot),
        status,
        1,
      ),
      testHistory: payload.testHistory || [],
      createdOn: payload.createdOn,
      createdBy: payload.createdBy,
      modifiedOn: payload.modifiedOn,
      modifiedBy: payload.modifiedBy,
    };
    return [...allRules.filter((r) => r.id !== record.id), record];
  }

  if (normalizeFlaggingRuleStatus(publishedBaseline.status) === 'active') {
    const changes = diffRuleSnapshots(getPublishedRule(publishedBaseline), snapshot);
    const record = {
      ...publishedBaseline,
      pendingDraft: { ...snapshot, savedOn: payload.modifiedOn, savedBy: payload.modifiedBy },
      configurationHistory: appendConfigurationHistory(
        publishedBaseline,
        'Save Draft Revision',
        changes,
        'draft',
        publishedBaseline.version || 1,
      ),
      testHistory: payload.testHistory || publishedBaseline.testHistory || [],
      modifiedOn: payload.modifiedOn,
      modifiedBy: payload.modifiedBy,
    };
    return allRules.map((r) => (r.id === record.id ? record : r));
  }

  const changes = diffRuleSnapshots(normalizeFlaggingForm(publishedBaseline), snapshot);
  const record = {
    ...normalizeFlaggingForm(publishedBaseline),
    ...snapshot,
    id: publishedBaseline.id,
    version: publishedBaseline.version || 1,
    status,
    pendingDraft: null,
    configurationHistory: appendConfigurationHistory(
      publishedBaseline,
      'Save Draft',
      changes,
      status,
      publishedBaseline.version || 1,
    ),
    testHistory: payload.testHistory || publishedBaseline.testHistory || [],
    modifiedOn: payload.modifiedOn,
    modifiedBy: payload.modifiedBy,
  };
  return allRules.map((r) => (r.id === record.id ? record : r));
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const prev = publishedBaseline || {};
  const isNew = !prev.id;
  const prevPublished = getPublishedRule(prev) || {};
  const changes = diffRuleSnapshots(prevPublished, snapshot);

  let nextVersion = 1;
  if (!isNew) {
    if (normalizeFlaggingRuleStatus(prev.status) === 'active') nextVersion = (prev.version || 1) + 1;
    else if (normalizeFlaggingRuleStatus(prev.status) === 'inactive') nextVersion = (prev.version || 1) + 1;
    else nextVersion = Math.max(1, prev.version || 1);
  }

  const record = {
    ...prevPublished,
    ...snapshot,
    id: payload.id,
    version: nextVersion,
    status: 'active',
    pendingDraft: null,
    configurationHistory: appendConfigurationHistory(
      prev,
      'Activate',
      changes.length
        ? changes
        : [{ field: 'Status', previousValue: prev.status || '—', newValue: 'active' }],
      'active',
      nextVersion,
    ),
    testHistory: payload.testHistory || prev.testHistory || [],
    createdOn: payload.createdOn || prev.createdOn,
    createdBy: payload.createdBy || prev.createdBy,
    modifiedOn: payload.modifiedOn,
    modifiedBy: payload.modifiedBy,
  };

  return [...allRules.filter((r) => r.id !== record.id), record];
}

export function mergeRulesAfterInactivate(allRules, ruleId, actorLabel = ACTOR.name) {
  const nowIso = new Date().toISOString();
  return allRules.map((r) => {
    if (r.id !== ruleId) return r;
    return {
      ...r,
      status: 'inactive',
      configurationHistory: appendConfigurationHistory(
        r,
        'Inactivate',
        [{ field: 'Status', previousValue: 'active', newValue: 'inactive' }],
        'inactive',
        r.version || 1,
      ),
      modifiedOn: nowIso,
      modifiedBy: actorLabel,
    };
  });
}

export function mergeRulesAfterListActivate(allRules, ruleId, actorLabel = ACTOR.name) {
  const rule = allRules.find((r) => r.id === ruleId);
  if (!rule) return allRules;
  const published = getPublishedRule(rule);
  const nowIso = new Date().toISOString();
  const payload = {
    ...published,
    id: ruleId,
    modifiedOn: nowIso,
    modifiedBy: actorLabel,
    testHistory: rule.testHistory || [],
  };
  return mergeRulesAfterActivate(allRules, payload, rule);
}
